import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  AppState,
  CategoryFilterOption,
  ContractTypeOption,
  FilterOption,
  FilterState,
  RadiusFilterOption,
  Range,
  Region,
  SubcategoryOption,
} from '@immosky/types';
import { select, Store } from '@ngrx/store';
import { selectFilterState } from '@immosky/object-search-filter';
import { Subject } from 'rxjs';

@Component({
  selector: 'lib-filter-chips',
  templateUrl: './filter-chips.component.html',
  styleUrls: ['./filter-chips.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterChipsComponent implements AfterViewInit, OnDestroy {
  private readonly CLOSED_HEIGHT = 48;

  private destroy$ = new Subject();

  filterState!: FilterState;
  isOpen = false;

  moreCount = 0;

  canExpand = false;

  @Input()
  alwaysOpen = false;

  @ViewChild('container')
  chipsContainer!: ElementRef<HTMLElement>;

  @ViewChild('list')
  chipsList!: ElementRef<HTMLElement>;
  listItems: HTMLLIElement[] = [];

  constructor(
    private cdRef: ChangeDetectorRef,
    private store: Store<AppState>
  ) {
    this.store
      .pipe(select(selectFilterState))
      .subscribe((filterState: FilterState) => {
        this.filterState = filterState;
        this.cdRef.markForCheck();
      });
  }

  ngAfterViewInit(): void {
    const loop = () => {
      this.updateCanExpand();

      if (!this.destroy$.isStopped) {
        window.requestAnimationFrame(loop);
      }
    };
    window.requestAnimationFrame(loop);

    this.countItemsBelowFirstRow();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateCanExpand() {
    const listHeight = this.chipsList?.nativeElement.clientHeight || 0;
    const canExpand = listHeight > this.CLOSED_HEIGHT && !this.alwaysOpen;

    if (this.canExpand !== canExpand) {
      this.canExpand = canExpand;

      window.requestAnimationFrame(this.countItemsBelowFirstRow.bind(this));
      this.cdRef.markForCheck();
    }
  }

  toggleOpen() {
    this.isOpen = !this.isOpen && this.canExpand;
    window.requestAnimationFrame(this.countItemsBelowFirstRow.bind(this));
  }

  private countItemsBelowFirstRow() {
    this.listItems = Array.from(
      this.chipsList.nativeElement.querySelectorAll('li')
    );
    const containerRect =
      this.chipsContainer.nativeElement.getBoundingClientRect();
    let count = 0;

    for (const li of this.listItems) {
      const liRect = li.getBoundingClientRect();

      if (containerRect.bottom <= liRect.top) {
        count++;
      }
    }

    if (this.moreCount !== count) {
      this.moreCount = count;
      this.cdRef.markForCheck();

      // recursively count until count is stable: same number two times in a row
      window.requestAnimationFrame(this.countItemsBelowFirstRow.bind(this));
    }
  }

  private formatRange(range: Range<FilterOption>) {
    return `${range.min.text} - ${range.max.text}`;
  }

  get regions(): Region[] {
    return this.filterState.region.regions;
  }

  get shouldDisplayRegions() {
    return this.filterState.region.isActive;
  }

  get radius(): RadiusFilterOption {
    return this.filterState.radius.filter;
  }

  get shouldDisplayRadius() {
    return this.filterState.radius.isActive && this.regions.length < 2;
  }

  get contractType(): ContractTypeOption {
    return this.filterState.contractType.filter;
  }

  get mainCategory(): CategoryFilterOption {
    return this.filterState.category.main.filter;
  }

  get shouldDisplayMainCategory() {
    return this.filterState.category.main.isActive;
  }

  get subcategories(): SubcategoryOption[] {
    return this.filterState.category.sub.filter[this.mainCategory.value].filter(
      (subcategoryOption: SubcategoryOption) => {
        return subcategoryOption.isChecked;
      }
    );
  }

  get shouldDisplaySubcategories() {
    return this.shouldDisplayMainCategory;
  }

  get price(): string {
    return this.formatRange(this.filterState.price.filter);
  }

  get shouldDisplayPrice() {
    return this.filterState.price.isActive;
  }

  get roomCount(): string {
    return this.formatRange(this.filterState.roomCount.filter);
  }

  get shouldDisplayRoomCount() {
    return (
      this.filterState.roomCount.isDisplayed &&
      this.filterState.roomCount.isActive
    );
  }

  get livingSpace(): string {
    return this.formatRange(this.filterState.livingSpace.filter);
  }

  get shouldDisplayLivingSpace() {
    return (
      this.filterState.livingSpace.isDisplayed &&
      this.filterState.livingSpace.isActive
    );
  }

  get propertySpace(): string {
    return this.formatRange(this.filterState.propertySpace.filter);
  }

  get shouldDisplayPropertySpace() {
    return (
      this.filterState.propertySpace.isDisplayed &&
      this.filterState.propertySpace.isActive
    );
  }

  get usageSpace(): string {
    return this.formatRange(this.filterState.usageSpace.filter);
  }

  get shouldDisplayUsageSpace() {
    return (
      this.filterState.usageSpace.isDisplayed &&
      this.filterState.usageSpace.isActive
    );
  }
}
